import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import { authMeta } from './meta';
import AltoleapAccountsRouter from '@vue-altoleap-libraries/vue-altoleap-accounts-ui/src/router';
import { AccountsRoutes } from '@vue-altoleap-libraries/vue-altoleap-accounts-ui/src/router/index';

// META
import Meta from 'vue-meta';

Vue.use(Meta, {
  keyName: 'metaInfo',
  attribute: 'data-vue-meta',
  ssrAttribute: 'data-vue-meta-ssr',
  tagIDKeyName: 'vmid',
  refreshOnceOnNavigation: true
});

import scrollBehavior from './scroll-behavior';

// TOKEN
import { TokenService } from '@/services/token';

// Utilities
import { layout, root, route, subRoute } from './utils';

Vue.use(VueRouter);

const routes = root([
  // Redirecting because the adapter in the backend doesn't have a override for url passed down
  {
    path: 'overview',
    name: 'dashboard',
    redirect: { name: 'home' }
  },
  ...AltoleapAccountsRouter.AuthRedirectRoutes,

  {
    path: 'auth',
    redirect: { name: 'sign-in' },
    props: true
  },

  subRoute({
    path: 'auth/sign-in',
    name: 'sign-in',
    component: 'views/auth/EmailEntry'
  }),

  subRoute({
    path: 'auth/legacy-sign-in',
    name: 'legacy-sign-in',
    component: 'views/auth/SignInWizard'
  }),

  subRoute({
    path: 'auth/password-reset',
    name: 'password-reset',
    component: 'views/auth/PasswordReset'
  }),

  subRoute({
    path: 'microsoft-callback',
    name: 'microsoft-callback',
    component: 'views/auth/MicrosoftCallback',
    meta: { public: true }
  }),
  {
    ...AltoleapAccountsRouter.AuthRoutes,
    children: AltoleapAccountsRouter.AuthRoutes.children!.map(
      (routeGroup: RouteConfig) => ({
        ...routeGroup,
        children: routeGroup.children!.filter(route => {
          return !['sign-in', 'password-reset'].includes(route.name!);
        })
      })
    )
  },

  layout(
    '',
    'app',
    [
      route('', 'home', [], authMeta),

      AccountsRoutes,

      route(
        'csi',
        'csi',
        [
          subRoute({
            path: 'items',
            name: 'item-list',
            component: 'views/items/ItemListPage',
            meta: { ...authMeta, label: 'Items' }
          }),

          subRoute({
            path: 'items-snapshots',
            name: 'item-snapshot-list',
            component: 'views/items/PriceSnapshotListPage',
            meta: { ...authMeta, label: 'Pricing Snapshots' }
          }),

          subRoute({
            path: 'items-tags',
            name: 'item-tag-list',
            component: 'views/items/ItemTagListPage',
            meta: { ...authMeta, label: 'Item Tags' }
          }),

          subRoute({
            path: 'csi-item-translations',
            name: 'csi-translation-list',
            component: 'views/csi/CsiTranslationsItemListPage',
            meta: { ...authMeta, label: 'Item Translations' }
          }),

          subRoute({
            path: 'item-commercial-translation',
            name: 'item-commercial-translation-list',
            component: 'views/csi/ItemCommercialTranslationListPage',
            meta: {
              label: 'Item Commercial Translation',
              breadcrumb: 'Item Commercial Translations'
            }
          }),

          subRoute({
            path: 'boms',
            name: 'bom-list',
            component: 'views/csi/BomsItemListPage',
            meta: { ...authMeta, label: 'Bill of Materials' }
          }),

          subRoute({
            path: 'customers/',
            name: 'customer-list',
            component: 'views/customers/CustomerListPage',
            meta: { label: 'Customers' }
          }),

          subRoute({
            path: 'customers/:customerId/',
            name: 'customer-detail',
            component: 'views/customers/CustomerAddressListPage',
            meta: { label: 'Customer Detail' }
          }),

          subRoute({
            path: 'vendors',
            name: 'vendor-list',
            component: 'views/vendors/VendorListPage',
            meta: { label: 'Vendors' }
          })
        ],
        authMeta,
        { name: 'item-list' }
      ),

      route(
        'quotes',
        'quotes',
        [
          subRoute({
            path: 'proposals/',
            name: 'quote-list',
            component: 'views/quotes/QuoteListPage',
            meta: { label: 'Quotes' }
          }),

          subRoute({
            path: 'proposals/form/:quoteId?/revision/:quoteRevision?',
            name: 'quote-form',
            component: 'views/quotes/QuoteFormPage',
            meta: { label: 'Quote Form' }
          }),

          subRoute({
            path: 'proposals/:quoteId/revision/:quoteRevision',
            name: 'quote-revision-detail',
            component: 'views/quotes/QuoteDetailPage',
            meta: { label: 'Quote Detail' }
          }),

          subRoute({
            path: 'templates/services',
            name: 'quote-service',
            component: 'views/quotes/quote-service/QuoteServiceContainer',
            meta: { label: 'Quote Service' },
            children: [
              subRoute({
                path: '',
                name: 'quote-service-list',
                component:
                  'views/quotes/quote-service/QuoteServiceTemplateListPage',
                meta: { label: 'Service Templates' }
              }),
              subRoute({
                path: 'variables/',
                name: 'quote-service-variables',
                component:
                  'views/quotes/quote-service/QuoteServiceVariableListPage',
                meta: { label: 'Service Variables' }
              })
            ],
            redirect: { name: 'quote-service-list' }
          }),

          subRoute({
            path: 'templates/freights',
            name: 'quote-freight-list',
            component: 'views/quotes/QuoteFreightTemplateListPage',
            meta: { label: 'Freight Templates' }
          }),

          subRoute({
            path: 'templates/terms',
            name: 'quote-term-list',
            component: 'views/quotes/QuoteTermTemplateListPage',
            meta: { label: 'Terms & Conditions Templates' }
          }),

          subRoute({
            path: 'templates/payment-terms',
            name: 'quote-payment-terms-list',
            component:
              'views/quotes/quote-payment-term/QuotePaymentTermTemplateListPage',
            meta: { label: 'Payment Terms Templates' }
          }),

          subRoute({
            path: 'exchange-rates',
            name: 'exchange-rates',
            component: 'views/currency/ExchangeRates',
            meta: { ...authMeta, label: 'Exchange Rates' }
          }),

          subRoute({
            path: 'agents',
            name: 'agents',
            component: 'views/agents/AgentListPage',
            meta: { ...authMeta, label: 'Agents' }
          })
        ],
        authMeta,
        { name: 'quote-list' }
      ),

      route(
        'tariffs',
        'tariffs',
        [
          subRoute({
            path: 'tariffs',
            name: 'tariff-list',
            component: 'views/tariffs/TariffListPage',
            meta: { label: 'Tariff Rates' }
          }),

          subRoute({
            path: 'regions',
            name: 'tariff-regions-list',
            component: 'views/tariffs/TariffRegionListPage',
            meta: { label: 'Tariff Regions' }
          }),

          subRoute({
            path: 'mandatory-items',
            name: 'tariff-mandatory-items-list',
            component: 'views/tariffs/TariffMandatoryItemListPage',
            meta: { label: 'Mandatory Items' }
          }),

          subRoute({
            path: 'exceptions',
            name: 'tariff-exceptions-list',
            component: 'views/tariffs/TariffExceptionListPage',
            meta: { label: 'Tariff Exceptions' }
          }),

          subRoute({
            path: 'harmonized-system/codes',
            name: 'hscode-list',
            component: 'views/harmonized-system/HSCodeListPage',
            meta: { label: 'HS Codes' }
          })
        ],
        authMeta,
        { name: 'tariff-list' }
      ),

      route(
        'orders',
        'orders',
        [
          subRoute({
            path: '',
            name: 'order-list',
            component: 'views/orders/OrderListPage',
            meta: { label: 'Orders' }
          }),

          subRoute({
            path: ':series/',
            name: 'order-detail',
            component: 'views/orders/OrderDetailPage',
            meta: { label: 'Orders Detail' }
          }),

          subRoute({
            path: ':series/price-distribution/',
            name: 'price-distribution',
            component: 'views/orders/PriceDistributionListPage',
            meta: { label: 'Price Distribution' }
          })
        ],
        authMeta,
        { name: 'order-list' }
      ),

      subRoute({
        path: 'products/configurator/',
        name: 'configuration-list',
        component: 'views/configurator/ConfigurationListPage',
        meta: { label: 'Configurations' }
      }),

      route(
        'configurator',
        'configurator',
        [
          subRoute({
            path: 'modules/',
            name: 'module-list',
            component: 'views/configurator/modules/ModuleListPage',
            meta: { label: 'Product Modules' }
          }),

          subRoute({
            path: 'modules/:moduleId/',
            name: 'module-detail',
            component: 'views/configurator/modules/ModuleDetailPage',
            meta: { label: 'Module Detail' }
          }),

          subRoute({
            path: 'tags/',
            name: 'tags-list',
            component: 'views/configurator/modules/TagListPage',
            meta: { label: 'Configurator Tags' }
          }),

          subRoute({
            path: 'configurations/:configurationId',
            name: 'configuration-detail',
            component: 'views/configurator/ConfigurationDetailPage',
            meta: { label: 'Configuration Detail' }
          })
        ],
        authMeta,
        { name: 'module-list' }
      ),

      route(
        'requisitions',
        'requisitions',
        [
          subRoute({
            path: 'purchase',
            name: 'requisition-list',
            component: 'views/requisitions/RequisitionListPage',
            meta: { label: 'Purchase Requisitions' }
          }),

          subRoute({
            path: 'purchase/:id/',
            name: 'requisition-detail',
            component: 'views/requisitions/RequisitionDetailPage',
            meta: { label: 'Requisition Detail' }
          }),

          subRoute({
            path: 'inventory-advance-payment',
            name: 'inventory-advance-payment-list',
            component:
              'views/inventory-advance-payment/InventoryAdvancePaymentListPage',
            meta: { label: 'Inventory Advance Payments' }
          }),

          subRoute({
            path: 'inventory-advance-payment/:id/',
            name: 'inventory-advance-payment-detail',
            component:
              'views/inventory-advance-payment/InventoryAdvancePaymentDetailPage',
            meta: { label: 'Inventory Advance Payments Detail' }
          }),

          subRoute({
            path: 'payment',
            name: 'payment-requisition-list',
            component: 'views/payment-requisition/PaymentRequisitionListPage',
            meta: { label: 'Payment Requisition' }
          }),

          subRoute({
            path: 'payment/:id/',
            name: 'payment-requisition-detail',
            component: 'views/payment-requisition/PaymentRequisitionDetailPage',
            meta: { label: 'Payment Requisition Detail' }
          })
        ],
        authMeta,
        { name: 'requisition-list' }
      ),

      route(
        'payroll-change-request',
        'payroll-change-request',
        [
          subRoute({
            path: '',
            name: 'payroll-change-request-list',
            component:
              'views/payroll-change-request/PayrollChangeRequestListPage',
            meta: { label: 'Payroll Change Requisitions' }
          }),

          subRoute({
            path: ':id/',
            name: 'payroll-change-request-detail',
            component:
              'views/payroll-change-request/PayrollChangeRequestDetailPage',
            meta: { label: 'Payroll Change Requisition Detail' }
          })
        ],
        authMeta,
        { name: 'payroll-change-request' }
      ),

      route(
        'hr',
        'hr',
        [
          subRoute({
            path: 'employees',
            name: 'employee-list',
            component: 'views/hr/EmployeeListPage',
            meta: { label: 'Employees' }
          }),

          subRoute({
            path: 'policies',
            name: 'time-off-policy',
            component: 'views/time-off/TimeOffPolicyListPage',
            meta: { label: 'Time Off Policies' }
          }),

          subRoute({
            path: 'holidays',
            name: 'holiday-list',
            component: 'views/hr/HolidayListPage',
            meta: { label: 'Company Holidays' }
          })
        ],
        authMeta,
        { name: 'employee-list' }
      ),

      route(
        'invoice',
        'invoices',
        [
          subRoute({
            path: 'invoices',
            name: 'invoice-list',
            component: 'views/invoices/InvoiceListPage',
            meta: { label: 'Invoices' }
          }),

          subRoute({
            path: 'invoices/:id',
            name: 'invoice-detail',
            component: 'views/invoices/InvoiceDetailPage',
            meta: { label: 'Invoice Detail' }
          }),

          subRoute({
            path: 'payment-plans/:id',
            name: 'payment-plan-detail',
            component: 'views/payment-plan/PaymentPlanDetailPage',
            meta: { label: 'Payment Plan Detail' }
          }),

          subRoute({
            path: 'payment-plans',
            name: 'payment-plan-list',
            component: 'views/payment-plan/PaymentPlanListPage',
            meta: { label: 'Payment Plans' }
          }),

          subRoute({
            path: 'banks',
            name: 'bank-list',
            component: 'views/bank/BankListPage',
            meta: { label: 'Banking Information' }
          }),

          subRoute({
            path: 'taxes',
            name: 'tax-list',
            component: 'views/tax/TaxListPage',
            meta: { label: 'Tax Information' }
          })
        ],
        authMeta,
        { name: 'invoice-list' }
      ),

      route(
        'time-off',
        'time-off',
        [
          subRoute({
            path: 'requests',
            name: 'time-off-calendar',
            component: 'views/time-off/TimeOffRequestPage',
            meta: { label: 'Time Off Requests' }
          }),
          subRoute({
            path: 'balances',
            name: 'time-off-balance',
            component: 'views/time-off/TimeOffBalanceListPage',
            meta: { label: 'Time Off Balances' }
          }),
          subRoute({
            path: 'overtime-requests',
            name: 'overtime-request-list',
            component: 'views/overtime-request/OvertimeRequestListPage',
            meta: { label: 'Lieu Time Submissions' }
          }),
          subRoute({
            path: 'vacation-carry-over-requests',
            name: 'vacation-carry-over-request-list',
            component:
              'views/vacation-carry-over-request/VacationCarryOverRequestListPage',
            meta: { label: 'Vacation Carry-Over Exception' }
          })
        ],
        authMeta,
        { name: 'time-off-list' }
      ),

      route(
        'employee-forms',
        'employee-form',
        [
          subRoute({
            path: '',
            name: 'form-submission-list',
            component: 'views/employee-form/EmployeeFormListPage',
            meta: { label: 'Employee Forms' }
          }),

          subRoute({
            path: ':id/',
            name: 'form-request-detail',
            component: 'views/employee-form/EmployeeFormSubmissionDetailPage',
            meta: { label: 'Employee Detail Forms' }
          })
        ],
        authMeta,
        { name: 'form-request' }
      ),

      route(
        'contracts',
        'contracts',
        [
          subRoute({
            path: '',
            name: 'contract-list',
            component: 'views/contracts/ContractListPage',
            meta: { label: 'Contracts' }
          }),
          subRoute({
            path: ':id/',
            name: 'contract-detail',
            component: 'views/contracts/ContractDetailPage',
            meta: { label: 'Contract Detail' }
          })
        ],
        authMeta,
        { name: 'contract-list' }
      ),

      route(
        'app-builder',
        'app-builder',
        [
          route(
            'legacy-forms',
            'form-builder',
            [
              subRoute({
                path: '',
                name: 'form-template-list',
                component: 'views/form-builder/FormBuilderListPage',
                meta: { label: 'Form Builder Templates' }
              }),

              subRoute({
                path: ':id/',
                name: 'form-template-detail',
                component: 'views/form-builder/FormBuilderDetailPage',
                meta: { label: 'Form Builder Template Detail' }
              })
            ],
            authMeta,
            { name: 'form-request' }
          ),

          route(
            'documents',
            'document',
            [
              subRoute({
                path: 'marketing',
                name: 'document-marketing-list',
                component: 'views/document/DocumentListPage',
                meta: { label: 'Marketing Documents' }
              }),
              subRoute({
                path: 'tags',
                name: 'document-tag-list',
                component: 'views/document/DocumentTagListPage',
                meta: { label: 'Document Tags' }
              })
            ],
            authMeta,
            { name: 'document-marketing-list' }
          ),

          route(
            'workflows',
            'workflows',
            [
              subRoute({
                path: '',
                name: 'workflow-list',
                component: 'views/workflows/WorkflowListPage',
                meta: { label: 'Workflows' }
              }),
              subRoute({
                path: ':id/',
                name: 'workflow-detail',
                component: 'views/workflows/WorkflowDetailPage',
                meta: { label: 'Workflow Detail' }
              })
            ],
            authMeta,
            { name: 'workflow-list' }
          ),

          route(
            'templates',
            'workflow-form-builder',
            [
              subRoute({
                path: '',
                name: 'workflow-form-template-list',
                component:
                  'views/workflow-form-builder/WorkflowFormBuilderListPage',
                meta: { label: 'Form Builder Templates' }
              }),

              subRoute({
                path: ':templateId/revisions/:revisionId/',
                name: 'workflow-form-template-detail',
                component:
                  'views/workflow-form-builder/WorkflowFormBuilderDetailPage',
                meta: { label: 'Workflow Form Builder Template Detail' }
              })
            ],
            authMeta,
            { name: 'workflow-form-template-list' }
          )
        ],
        authMeta,
        { name: 'workflows' }
      ),
      route(
        'apps',
        'workflow-forms',
        [
          subRoute({
            path: ':id/',
            name: 'workflow-form-submission-list',
            component: 'views/workflow-forms/WorkflowFormListPage',
            meta: { label: 'Workflow Forms' }
          }),
          subRoute({
            path: ':templateId/detail/:id/',
            name: 'workflow-form-request-detail',
            component: 'views/workflow-forms/WorkflowFormDetailPage',
            meta: { label: 'Workflow Detail Forms' }
          })
        ],
        authMeta,
        { name: 'workflow-form-submission-list' }
      )
    ],
    { name: 'home' },
    authMeta
  ),

  subRoute({
    path: '*',
    name: '404',
    component: 'views/common/404',
    meta: { label: '404' }
  })
]);

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior
});

const tokenService = new TokenService();

AltoleapAccountsRouter.AuthGuard(router, tokenService);

export default router;
